import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};

const defaultDurationMs = 3500;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback((toast) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const durationMs = Number.isFinite(Number(toast?.durationMs)) ? Number(toast.durationMs) : defaultDurationMs;

    const next = {
      id,
      type: toast?.type || 'info',
      title: toast?.title || '',
      message: toast?.message || '',
      durationMs
    };

    setToasts((prev) => [next, ...prev].slice(0, 4));

    if (durationMs > 0) {
      window.setTimeout(() => {
        dismissToast(id);
      }, durationMs);
    }

    return id;
  }, [dismissToast]);

  const value = useMemo(() => ({ pushToast, dismissToast }), [pushToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

const ToastViewport = ({ toasts, onDismiss }) => {
  if (!toasts.length) return null;

  return (
    <div className="toast-viewport" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast-${t.type}`}
          role="status"
          aria-live="polite"
        >
          <div className="toast-body">
            {t.title ? <div className="toast-title">{t.title}</div> : null}
            {t.message ? <div className="toast-message">{t.message}</div> : null}
          </div>
          <button
            type="button"
            className="toast-close"
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};
